const COMMON_EMAIL_DOMAINS = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "live.com",
    "msn.com",
    "mail.com",
    "aol.com",
    "proton.me",
    "protonmail.com",
    "yandex.com"
]

const COMMON_DOMAIN_TYPOS: Record<string, string> = {
    // gmail
    "gkail.com": "gmail.com",
    "gamil.com": "gmail.com",
    "gmial.com": "gmail.com",
    "gmaill.com": "gmail.com",
    "gmaiil.com": "gmail.com",
    "gmaik.com": "gmail.com",
    "gnail.com": "gmail.com",
    "gmall.com": "gmail.com",
    "gmailcom": "gmail.com",
    "gmail.con": "gmail.com",
    "gmail.comm": "gmail.com",
    "gmai.com": "gmail.com",
    "gmail.om": "gmail.com",
    "gmail.xom": "gmail.com",
    "gmail.co": "gmail.com",
    "gmail.cmo": "gmail.com",
    "gmail.cm": "gmail.com",

    // yahoo
    "yahooo.com": "yahoo.com",
    "yaahoo.com": "yahoo.com",
    "yahoho.com": "yahoo.com",
    "yahoo.con": "yahoo.com",
    "yahoo.comm": "yahoo.com",
    "yahoo.om": "yahoo.com",
    "yahoo.cmo": "yahoo.com",
    "yahoo.cm": "yahoo.com",
    "yhoo.com": "yahoo.com",
    "yaoo.com": "yahoo.com",
    "yahho.com": "yahoo.com",
    "yahooo.co": "yahoo.com",
    "yaho.com": "yahoo.com",
    "yahoo.co": "yahoo.com",

    // outlook
    "outlok.com": "outlook.com",
    "outllok.com": "outlook.com",
    "outlookk.com": "outlook.com",
    "otlook.com": "outlook.com",
    "outloo.com": "outlook.com",
    "outook.com": "outlook.com",
    "outlook.con": "outlook.com",
    "outlook.comm": "outlook.com",
    "outlook.co": "outlook.com",
    "outlook.cm": "outlook.com",

    // hotmail
    "hotnail.com": "hotmail.com",
    "hotmial.com": "hotmail.com",
    "hotmai.com": "hotmail.com",
    "hotmal.com": "hotmail.com",
    "hotmil.com": "hotmail.com",
    "hotmali.com": "hotmail.com",
    "hotmaill.com": "hotmail.com",
    "hotmail.con": "hotmail.com",
    "hotmail.comm": "hotmail.com",
    "hotmail.co": "hotmail.com",
    "hotmail.cm": "hotmail.com",

    // icloud
    "iclod.com": "icloud.com",
    "icluod.com": "icloud.com",
    "iclud.com": "icloud.com",
    "icloud.con": "icloud.com",
    "icloud.comm": "icloud.com",
    "icloud.co": "icloud.com",
    "icloud.cm": "icloud.com",

    // live / msn / aol / mail
    "lve.com": "live.com",
    "liv.com": "live.com",
    "live.co": "live.com",
    "live.con": "live.com",
    "msn.co": "msn.com",
    "msn.con": "msn.com",
    "mail.co": "mail.com",
    "mail.con": "mail.com",
    "aol.co": "aol.com",
    "aol.con": "aol.com",
    "ao.com": "aol.com",

    // proton
    "protonmai.com": "protonmail.com",
    "protonmail.co": "protonmail.com",
    "protonmail.con": "protonmail.com",
    "protomail.com": "protonmail.com",
    "prot0nmail.com": "protonmail.com",
    "proton.ne": "proton.me",
    "proton.mw": "proton.me",
    "proton.mee": "proton.me",

    // yandex
    "yandx.com": "yandex.com",
    "yandec.com": "yandex.com",
    "yandex.co": "yandex.com",
    "yandex.con": "yandex.com"
}

const getLevenshteinDistance = (a: string, b: string): number => {
    if (a === b) return 0
    if (!a.length) return b.length
    if (!b.length) return a.length

    const matrix: number[][] = Array.from({length: a.length + 1}, () => new Array(b.length + 1).fill(0))

    for (let i = 0; i <= a.length; i++) matrix[i][0] = i
    for (let j = 0; j <= b.length; j++) matrix[0][j] = j

    for (let i = 1; i <= a.length; i++) {
        for (let j = 1; j <= b.length; j++) {
            const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1
            matrix[i][j] = Math.min(
                matrix[i - 1][j] + 1,
                matrix[i][j - 1] + 1,
                matrix[i - 1][j - 1] + substitutionCost
            )
        }
    }

    return matrix[a.length][b.length]
}

export const getSuggestedEmail = (email: string): string | null => {
    const normalizedEmail = email.trim().toLowerCase()
    const atIndex = normalizedEmail.lastIndexOf("@")

    if (atIndex <= 0 || atIndex === normalizedEmail.length - 1) {
        return null
    }

    const localPart = normalizedEmail.slice(0, atIndex)
    const domainPart = normalizedEmail.slice(atIndex + 1)

    if (!localPart || !domainPart || !domainPart.includes(".")) {
        return null
    }

    const typoMatch = COMMON_DOMAIN_TYPOS[domainPart]
    if (typoMatch) {
        return `${localPart}@${typoMatch}`
    }

    let bestMatch: string | null = null
    let bestDistance = Number.MAX_SAFE_INTEGER

    for (const candidateDomain of COMMON_EMAIL_DOMAINS) {
        const distance = getLevenshteinDistance(domainPart, candidateDomain)
        if (distance < bestDistance) {
            bestDistance = distance
            bestMatch = candidateDomain
        }
    }

    if (!bestMatch || bestMatch === domainPart) {
        return null
    }

    const maxAllowedDistance = domainPart.length <= 8 ? 1 : 2
    if (bestDistance > maxAllowedDistance) {
        return null
    }

    return `${localPart}@${bestMatch}`
}
